import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription } from '../components/ui/alert';
import ImageUpload from '../components/ImageUpload';
import GoogleLocationPicker from '../components/GoogleLocationPicker';
import VehicleRegLookup from '../components/VehicleRegLookup';
import { locations } from '../mock';
import { AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PostAd = () => {
  const navigate = useNavigate();
  const { country, countryConfig } = useCountry();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    price: '',
    location: '',
    condition: '',
    negotiable: true,
    phone: '',
    featured: false,
    promoted: false
  });
  const [locationData, setLocationData] = useState({
    latitude: 6.9271,
    longitude: 79.8612,
    address: ''
  });
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableSubcategories, setAvailableSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [maxImages, setMaxImages] = useState(9); // admin-configurable, default 9
  const [vehicleSpecs, setVehicleSpecs] = useState({});

  // Fetch admin image limit
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/settings/image-limit`)
      .then(r => setMaxImages(r.data?.max_images_per_listing || 9))
      .catch(() => setMaxImages(9));
  }, []);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/categories/`);
        const cats = response.data.categories || response.data || [];
        setCategories(cats);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Check if user is logged in
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login to post an ad');
        navigate('/login');
        return;
      }

      // Validate images
      if (images.length === 0) {
        setError('Please upload at least one image');
        setLoading(false);
        return;
      }

      // Step 1: Upload images first
      const formDataImages = new FormData();
      images.forEach(image => {
        formDataImages.append('images', image);
      });

      const imageResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/images/upload`,
        formDataImages,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const imageUrls = imageResponse.data.images.map(img => img.url);

      // Step 2: Create listing with image URLs and location data
      const listingFormData = new FormData();
      listingFormData.append('title', formData.title);
      listingFormData.append('description', formData.description);
      listingFormData.append('category', formData.category);
      if (formData.subcategory) {
        listingFormData.append('subcategory', formData.subcategory);
      }
      listingFormData.append('price', formData.price);
      listingFormData.append('location', locationData.address || formData.location);
      listingFormData.append('latitude', locationData.latitude);
      listingFormData.append('longitude', locationData.longitude);
      listingFormData.append('condition', formData.condition);
      listingFormData.append('negotiable', formData.negotiable);
      listingFormData.append('phone', formData.phone);
      listingFormData.append('featured', formData.featured);
      listingFormData.append('promoted', formData.promoted);
      // Country isolation - tag ad with current site country
      if (country) listingFormData.append('country', country);

      // Vehicle specs (only if the seller filled anything)
      if (vehicleSpecs && Object.keys(vehicleSpecs).some(k => vehicleSpecs[k] !== '' && vehicleSpecs[k] != null)) {
        listingFormData.append('vehicle_details_json', JSON.stringify(vehicleSpecs));
      }
      
      // Add images as File objects
      images.forEach(image => {
        listingFormData.append('images', image);
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/listings/`,
        listingFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.id) {
        alert('Ad posted successfully!');
        navigate(`/listing/${response.data.id}`);
      }
    } catch (err) {
      console.error('Error posting ad:', err);
      setError(err.response?.data?.detail || 'Failed to post ad. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Post Your Ad
            </CardTitle>
            <CardDescription>Fill in the details below to create your listing</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                
                <div>
                  <Label htmlFor="title">Ad Title *</Label>
                  <Input
                    id="title"
                    type="text"
                    placeholder="e.g., iPhone 15 Pro Max - Like New"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    maxLength={200}
                    data-testid="post-ad-title"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your item in detail..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={6}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => {
                        // Find the selected category and get its subcategories
                        const selectedCat = categories.find(c => c.slug === value);
                        setAvailableSubcategories(selectedCat?.subcategories || []);
                        setFormData({ ...formData, category: value, subcategory: '' });
                      }} 
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="subcategory">Subcategory {availableSubcategories.length > 0 && '*'}</Label>
                    <Select 
                      value={formData.subcategory} 
                      onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                      disabled={availableSubcategories.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={availableSubcategories.length > 0 ? "Select subcategory" : "Select category first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSubcategories.map(sub => (
                          <SelectItem key={sub.slug} value={sub.slug}>{sub.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="condition">Condition *</Label>
                    <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select condition" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Brand New">Brand New</SelectItem>
                        <SelectItem value="Like New">Like New</SelectItem>
                        <SelectItem value="Used">Used</SelectItem>
                        <SelectItem value="For Parts">For Parts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Vehicle reg-plate auto-fill (only for Vehicles category) */}
              {formData.category === 'vehicles' && (
                <VehicleRegLookup value={vehicleSpecs} onChange={setVehicleSpecs} />
              )}

              {/* Price & Location */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Price & Location</h3>
                
                <div>
                  <Label htmlFor="price">Price ({countryConfig?.currency_symbol || 'Rs'}) *</Label>
                  <Input
                    id="price"
                    type="number"
                    placeholder="e.g., 450000"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    min="0"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="negotiable"
                    checked={formData.negotiable}
                    onCheckedChange={(checked) => setFormData({ ...formData, negotiable: checked })}
                  />
                  <Label htmlFor="negotiable" className="cursor-pointer">Price is negotiable</Label>
                </div>
              </div>

              {/* Location Section with Google Maps */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">📍 Location</h3>
                <GoogleLocationPicker 
                  value={locationData}
                  onChange={setLocationData}
                />
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Contact Information</h3>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+94771234567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">Buyers will contact you via this number</p>
                </div>
              </div>

              {/* Images */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Photos *</h3>
                <ImageUpload 
                  images={images}
                  onImagesChange={setImages}
                  maxImages={maxImages}
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || images.length === 0}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Posting...
                    </>
                  ) : (
                    'Post Ad'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PostAd;
